import cn from 'classnames';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl, MessageDescriptor } from 'react-intl';

import { templatesApi } from '@/shared/api/transcriptor/templatesApi';
import {
  Button,
  Input,
  Modal,
  Select,
  Textarea,
  toastCaller,
} from '@/shared/ui';
import { ValueType } from '@/shared/ui/Select/Select.types';

import {
  CatalogListing,
  getSpecialtyLabel,
  TEMPLATE_SPECIALTIES,
} from '@/entities/templates';

import { i18n } from '../../config';

import styles from './PublishTemplateDialog.module.scss';

const CATEGORY_OPTIONS: Array<{ label: MessageDescriptor; value: string }> = [
  { label: i18n.publishTemplateMessages.categoryNote, value: 'note' },
  { label: i18n.publishTemplateMessages.categoryDocument, value: 'document' },
];

const DEFAULT_CATEGORY = 'note';

type PublishTemplateDialogProps = {
  isOpen: boolean;
  templateName: string;
  templateId: string;
  isPublished?: boolean;
  listing?: CatalogListing | null;
  onClose: () => void;
  onPublished?: () => void;
};

export const PublishTemplateDialog: FC<PublishTemplateDialogProps> = (
  props,
) => {
  const {
    isOpen,
    templateName,
    templateId,
    isPublished = false,
    listing,
    onClose,
    onPublished,
  } = props;

  const { formatMessage } = useIntl();

  const [description, setDescription] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [category, setCategory] = useState(DEFAULT_CATEGORY);
  const [location, setLocation] = useState('');
  const [tags, setTags] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const listingRef = useRef(listing);
  useEffect(() => {
    listingRef.current = listing;
  });

  // Seed on the open transition, not on `listing`, so a refetch behind the
  // dialog cannot discard what the author is typing.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const currentListing = listingRef.current;

    setDescription(currentListing?.description?.trim() ?? '');
    setSpecialty(currentListing?.specialty ?? '');
    setCategory(currentListing?.category ?? DEFAULT_CATEGORY);
    setLocation(currentListing?.location ?? '');
    setTags(currentListing?.tags?.join(', ') ?? '');
  }, [isOpen]);

  const specialtyOptions = (
    specialty && !TEMPLATE_SPECIALTIES.includes(specialty)
      ? [...TEMPLATE_SPECIALTIES, specialty]
      : TEMPLATE_SPECIALTIES
  ).map((option) => ({
    label: getSpecialtyLabel(formatMessage, option),
    value: option,
  }));

  const categoryOptions = CATEGORY_OPTIONS.map((option) => ({
    label: formatMessage(option.label),
    value: option.value,
  }));

  const canSubmit = description.trim().length > 0 && specialty.length > 0;

  const handleSpecialtyChange = useCallback(
    (value: React.ChangeEvent<Element> | ValueType) => {
      if (typeof value === 'string') {
        setSpecialty(value);
      }
    },
    [],
  );

  const handleCategoryChange = useCallback(
    (value: React.ChangeEvent<Element> | ValueType) => {
      if (typeof value === 'string') {
        setCategory(value);
      }
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) {
      return;
    }

    setSubmitting(true);

    try {
      await templatesApi.publishCommunityTemplate({
        template_id: templateId,
        specialty,
        category,
        description: description.trim(),
        tags: tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        location: location.trim() || undefined,
      });

      toastCaller({
        type: 'success',
        message: formatMessage(
          isPublished
            ? i18n.publishTemplateMessages.republishSuccess
            : i18n.publishTemplateMessages.publishSuccess,
        ),
      });
      onClose();
      onPublished?.();
    } catch {
      toastCaller({
        type: 'error',
        message: formatMessage(i18n.publishTemplateMessages.publishError),
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    canSubmit,
    category,
    description,
    formatMessage,
    isPublished,
    location,
    onClose,
    onPublished,
    specialty,
    tags,
    templateId,
  ]);

  return (
    <Modal
      data-component="publish-template-dialog"
      isOpen={isOpen}
      onCancel={onClose}
      title={formatMessage(
        isPublished
          ? i18n.publishTemplateMessages.republishTitle
          : i18n.publishTemplateMessages.publishTitle,
      )}
      hideFooter
    >
      <div className={styles.content}>
        <p className={cn('p3', styles.subtitle)}>
          {formatMessage(
            isPublished
              ? i18n.publishTemplateMessages.republishDescription
              : i18n.publishTemplateMessages.publishDescription,
            { name: templateName },
          )}
        </p>

        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={formatMessage(
            i18n.publishTemplateMessages.descriptionPlaceholder,
          )}
          rows={3}
        />

        <div className={styles.row}>
          <Select
            label={`${formatMessage(i18n.publishTemplateMessages.specialty)} *`}
            options={specialtyOptions}
            value={specialty}
            onChange={handleSpecialtyChange}
          />
          <Select
            label={formatMessage(i18n.publishTemplateMessages.category)}
            options={categoryOptions}
            value={category}
            onChange={handleCategoryChange}
          />
        </div>

        <Input
          name="tags"
          label={formatMessage(i18n.publishTemplateMessages.tags)}
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder={formatMessage(
            i18n.publishTemplateMessages.tagsPlaceholder,
          )}
        />

        <Input
          name="location"
          label={formatMessage(i18n.publishTemplateMessages.location)}
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder={formatMessage(
            i18n.publishTemplateMessages.locationPlaceholder,
          )}
        />

        <div className={styles.divider} />

        <div className={styles.actions}>
          <Button variant="secondary" size="medium" onClick={onClose}>
            {formatMessage(i18n.publishTemplateMessages.cancel)}
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
          >
            {submitting
              ? formatMessage(i18n.publishTemplateMessages.publishing)
              : formatMessage(
                  isPublished
                    ? i18n.publishTemplateMessages.republish
                    : i18n.publishTemplateMessages.publish,
                )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
